import React, { Fragment, JSX } from 'react';
import { OptionProps, Select } from '@/components/Select';
import { InputText } from '@/components';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { FormInputNameAddress } from '../types';

interface StateProps {
  formData: FormData;
  formErrors: FormErrors;
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
  onChangeCEP: (value: string) => void;
}

type Props = StateProps & DispatchProps;

export const AddressContent: React.FC<Props> = ({
  formData,
  onChangeFormInput,
  onChangeCEP,
  formErrors,
}): JSX.Element => {
  const stateOptions: OptionProps[] = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapa' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceara' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espirito Santo' },
    { value: 'GO', label: 'Goias' },
    { value: 'MA', label: 'Maranhao' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Para' },
    { value: 'PB', label: 'Paraiba' },
    { value: 'PR', label: 'Parana' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piaui' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondonia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'Sao Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' },
  ];
  return (
    <Fragment>
      <h2 className="font-dmsans font-normal text-neutro-b-400 text-[28px]">Endereço</h2>
      <p className="font-dmsans text-base font-normal text-neutro-w-600 leading-[28px]">
        Precisamos do seu endereço apenas para confirmação de pagamento
      </p>
      <div className="md:w-[500px] mt-[20px]" id="divEndereco">
        <div className="mb-[20px]">
          <InputText
            name="zipCode"
            label="CEP"
            placeholder="00000-000"
            className={`appearance-none w-full
                              rounded-md text-heading leading-tight focus:outline-none
                              focus:shadow-outline text-xs font-dmsans font-normal`}
            value={formData[FormInputNameAddress.zipCode]}
            maxLength={9}
            onChange={e => {
              onChangeFormInput(FormInputNameAddress.zipCode)(e.target.value);
              onChangeCEP(e.target.value);
            }}
            error={formErrors.zipCode && formErrors.zipCode[0]}
          />
          <a
            href="#"
            className="underline text-base text-alert-info mt-[15px] block"
            onClick={() =>
              window.open('https://buscacepinter.correios.com.br/app/endereco/index.php')
            }
          >
            Não sei o meu CEP
          </a>
        </div>
        {formData[FormInputNameAddress.zipCode] && (
          <>
            <div className="mb-[20px] grid grid-cols-5 gap-5">
              <Select
                name="state"
                label="Estado"
                placeholder="Selecione..."
                value={formData[FormInputNameAddress.state]}
                onChange={e =>
                  onChangeFormInput(FormInputNameAddress.state)(e?.target.value as string)
                }
                className="col-span-2"
                error={formErrors.state && formErrors.state[0]}
                options={stateOptions}
              ></Select>
              <InputText
                name="city"
                label="Cidade"
                placeholder=""
                className={`appearance-none w-full
                      rounded-md text-heading leading-tight focus:outline-none
                      focus:shadow-outline text-xs font-dmsans font-normal col-span-3`}
                value={formData[FormInputNameAddress.city]}
                onChange={e => onChangeFormInput(FormInputNameAddress.city)(e.target.value)}
                error={formErrors.city && formErrors.city[0]}
              />
            </div>
            <div className="mb-[20px]">
              <InputText
                name="district"
                label="Bairro"
                placeholder=""
                className={`appearance-none w-full
                      rounded-md text-heading leading-tight focus:outline-none
                      focus:shadow-outline text-xs font-dmsans font-normal col-span-3`}
                value={formData[FormInputNameAddress.district]}
                onChange={e => onChangeFormInput(FormInputNameAddress.district)(e.target.value)}
                error={formErrors.district && formErrors.district[0]}
              />
            </div>
            <div className="mb-[20px] grid grid-cols-4 gap-5">
              <InputText
                name="street"
                label="Rua/Avenida"
                placeholder=""
                className={`appearance-none w-full
                      rounded-md text-heading leading-tight focus:outline-none
                      focus:shadow-outline text-xs font-dmsans font-normal col-span-3`}
                value={formData[FormInputNameAddress.street]}
                onChange={e => onChangeFormInput(FormInputNameAddress.street)(e.target.value)}
                error={formErrors.street && formErrors.street[0]}
              />
              <InputText
                name="number"
                label="Número"
                placeholder=""
                className={`appearance-none w-full
                      rounded-md text-heading leading-tight focus:outline-none
                      focus:shadow-outline text-xs font-dmsans font-normal`}
                value={formData[FormInputNameAddress.number]}
                onChange={e => onChangeFormInput(FormInputNameAddress.number)(e.target.value)}
                error={formErrors.number && formErrors.number[0]}
              />
            </div>
            <div className="mb-[20px]">
              <InputText
                name="complement"
                label="Complemento (opcional)"
                placeholder=""
                className={`appearance-none w-full
                      rounded-md text-heading leading-tight focus:outline-none
                      focus:shadow-outline text-xs font-dmsans font-normal`}
                value={formData[FormInputNameAddress.complement]}
                onChange={e => onChangeFormInput(FormInputNameAddress.complement)(e.target.value)}
                error={formErrors.complement && formErrors.complement[0]}
              />
            </div>
          </>
        )}
      </div>
    </Fragment>
  );
};
